/**
 * @file  src/helpers/notificationSettings.js
 * @brief Pure state logic for the notification settings toggles, extracted
 *        out of src/pages/Notifications.js so it's testable with plain Jest
 *        (this repo has no component-test infra, see notificationSettings.test.js).
 */

// Mirrors Gitcha-API's Notification_Settings.model.js NOTIFICATION_SETTINGS_FIELDS
// defaults exactly - keep these two lists in sync. Every one of these
// defaults is "on" except AccountActivity, because every gate the API
// checks treats a MISSING settings row as "send" (see e.g.
// redisNotifications.js's `!settings || settings.length === 0 ||
// settings[0].X == 1` checks). A user who has never saved a settings row
// is actively receiving all of these emails today - showing the toggles as
// off for that user would tell them the opposite of the truth on the one
// screen built to show it.
export const TOGGLE_DEFAULTS = {
  Introductions: true,
  AccountActivity: false,
  Messages: true,
  Matches: true,
  MatchRecap: true,
  MlsMatchDigest: true,
  StandoutMatch: true,
  UpgradeNudge: true,
  ListingSyndicated: true,
  ListingActivity: true,
};

/**
 * Builds the toggle state to render from the API's GET response row.
 * `apiRow` is `undefined`/`null`/`{}` for "no row yet" (new user), fetch
 * still pending, or a rejected fetch - all three cases fall back to the
 * same on-by-default state the API itself would apply, rather than the
 * misleading all-off state an empty object previously produced.
 * @param {object|undefined|null} apiRow
 * @returns {Record<string, boolean>}
 */
export function buildSettingsState(apiRow) {
  const next = { ...TOGGLE_DEFAULTS };
  if (apiRow) {
    Object.keys(TOGGLE_DEFAULTS).forEach((key) => {
      if (apiRow[key] !== undefined) {
        next[key] = apiRow[key] === 1;
      }
    });
  }
  return next;
}

/**
 * Resolves optimistic toggle state after a save attempt settles. On
 * success, keeps the optimistic value. On failure, reverts to whatever the
 * toggle was before the attempt - a failed save must never leave the UI
 * showing a preference that was never actually persisted.
 * @param {Record<string, boolean>} settings
 * @param {string} key
 * @param {boolean} optimisticValue
 * @param {boolean} previousValue
 * @param {boolean} saveSucceeded
 * @returns {Record<string, boolean>}
 */
export function applyToggleResult(
  settings,
  key,
  optimisticValue,
  previousValue,
  saveSucceeded
) {
  return {
    ...settings,
    [key]: saveSucceeded ? optimisticValue : previousValue,
  };
}

// The alert types a user can unsubscribe from - every TOGGLE_DEFAULTS key
// except AccountActivity, which is the billing/subscription preference and
// has no unsubscribe category on the API side (mirrors Gitcha-API's
// notificationCategories.js NOTIFICATION_CATEGORY_COLUMNS whitelist).
export const UNSUBSCRIBE_ELIGIBLE_KEYS = Object.keys(TOGGLE_DEFAULTS).filter(
  (key) => key !== "AccountActivity"
);

/**
 * True when every unsubscribe-eligible toggle is off - drives the "unsubscribe
 * from all" master toggle's checked state as a derived value rather than
 * separate stored state, so it can never drift out of sync with the
 * individual toggles.
 * @param {Record<string, boolean>} settings
 * @returns {boolean}
 */
export function isUnsubscribedFromAll(settings) {
  return UNSUBSCRIBE_ELIGIBLE_KEYS.every((key) => !settings[key]);
}

/**
 * Builds the save payload for the master toggle: every unsubscribe-eligible
 * key set to the same value. AccountActivity is never included - the master
 * toggle must not touch the billing preference.
 * @param {boolean} value
 * @returns {Record<string, boolean>}
 */
export function buildAllTogglePayload(value) {
  return UNSUBSCRIBE_ELIGIBLE_KEYS.reduce((acc, key) => {
    acc[key] = value;
    return acc;
  }, {});
}

/**
 * Converts the master toggle's target *checked* state (checked = "I am
 * unsubscribed from all") into the per-category toggle value that state
 * implies (a category is "on" exactly when the user is NOT unsubscribed
 * from everything - i.e. the inverse). Pulled out as its own named,
 * directly-tested function specifically because this inversion previously
 * lived as a bare `!` at the component's call site and got the direction
 * backwards there: `handleToggleAll(!allUnsubscribed)` fed the *checked*
 * value straight into `buildAllTogglePayload` as if it were the *toggle*
 * value, so clicking "unsubscribe from all" turned every category ON and
 * reported success. Every caller must route through this function instead
 * of inverting inline.
 * @param {boolean} shouldUnsubscribeAll
 * @returns {boolean} the toggle value to write to every eligible category
 */
export function toggleValueForUnsubscribeAll(shouldUnsubscribeAll) {
  return !shouldUnsubscribeAll;
}

/**
 * Resolves optimistic state after a master-toggle save settles. On success,
 * every eligible key lands on `value`. On failure, every eligible key
 * reverts to whatever it individually held before the attempt - some
 * toggles may already have been off, so a blanket revert-to-true would be
 * wrong; `previousValues` (captured before the optimistic update) is what
 * makes the revert exact.
 * @param {Record<string, boolean>} settings
 * @param {Record<string, boolean>} previousValues
 * @param {boolean} value
 * @param {boolean} saveSucceeded
 * @returns {Record<string, boolean>}
 */
export function applyAllToggleResult(
  settings,
  previousValues,
  value,
  saveSucceeded
) {
  if (saveSucceeded) {
    return { ...settings, ...buildAllTogglePayload(value) };
  }
  return { ...settings, ...previousValues };
}
